import { Pipe, PipeTransform } from '@angular/core';

/**
 * Splits an already-translated alert message into the lines it should be rendered as.
 *
 * Alert messages carry their own line breaks as `\n` inside a single translation value, so the
 * dialogs have to break them apart before rendering. Doing that inline in the template ran the
 * `split()` — and, for the error dialog, two `trim()` calls per line — on every change-detection
 * pass, and handed `*ngFor` a brand-new array each time.
 *
 * Deliberately a *pure* pipe: Angular memoises it against its inputs, so the work happens once per
 * distinct message and the same array reference is returned until the message actually changes.
 * The `translate` pipe still sits upstream, so a language switch produces a new string and the
 * lines are recomputed for it.
 *
 * @example
 *   {{ 'some.key' | translate | messageLines }}         // every line, blanks included
 *   {{ 'some.key' | translate | messageLines: true }}   // blank lines dropped
 */
@Pipe({
  name: 'messageLines',
  standalone: true,
  pure: true,
})
export class MessageLinesPipe implements PipeTransform {
  /**
   * @param message - The translated message, or null/undefined before it resolves.
   * @param dropBlank - When true, lines that are empty or whitespace-only are omitted. The
   *   surviving lines are returned untrimmed, so any indentation a translation carries is kept.
   * @returns The message's lines, in order; an empty array when there is no message.
   */
  transform(message: string | null | undefined, dropBlank: boolean = false): string[] {
    const lines = (message ?? '').split('\n');
    return dropBlank ? lines.filter((line) => line.trim().length > 0) : lines;
  }
}
